import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FormRenderer, parseFormDefinition } from "@njbsoft/form-renderer";
import {
  buttonClasses,
  Card,
  EmptyState,
  EmptyValue,
  LoadError,
  PageSkeleton,
  PageTitle,
} from "@/components/ui";
import { useActiveMembership, useMyCustomerContext } from "@/hooks/use-customer-context";
import { useCustomerSubmission } from "@/hooks/use-customer-tests";
import { AuthedImage } from "@/components/authed-image";
import { FacilityRefValue } from "@/components/tests/facility-ref-value";
import { OfficialReportCard } from "@/components/tests/official-report-card";
import { formatLongInstantDay } from "@/lib/dates";
import { historyLink } from "@/lib/destinations";
import { evidenceImages } from "@/lib/evidence-images";
import { uuidParam } from "@/lib/search-params";
import { testDay } from "@/lib/test-day";

export const Route = createFileRoute("/_customer/testing/history/$submissionId")({
  component: SubmissionDetailPage,
});

/**
 * One test or inspection, redacted for the customer: the day it was performed,
 * the official report card for an accepted test, the filed form rendered
 * read-only, and its photos. Its days read in the zone of the utility that
 * owns it: the membership naming the test's org, the active one only when none
 * does. An id that cannot be a submission is not found without a read.
 */
function SubmissionDetailPage() {
  const { t, i18n } = useTranslation("customer");
  const { submissionId } = Route.useParams();
  const id = uuidParam(submissionId) ?? "";
  const submissionQuery = useCustomerSubmission(id);
  const { data: context } = useMyCustomerContext();
  const active = useActiveMembership();
  const submission = submissionQuery.data;

  if (!id || submissionQuery.error?.status === 404) {
    return (
      <div
        role="alert"
        className="rounded-card border border-line bg-surface p-4 shadow-card lg:p-5"
      >
        <h1 className="text-lg font-semibold tracking-[-0.01em] text-ink">
          {t("history.detail.notFound")}
        </h1>
        <Link {...historyLink()} className={`mt-3 ${buttonClasses("secondary")}`}>
          {t("nav.tabs.history")}
        </Link>
      </div>
    );
  }

  if (submissionQuery.isError) {
    return (
      <LoadError
        asPage
        title={t("history.detail.error")}
        body={t("loadError.body")}
        onRetry={() => void submissionQuery.refetch()}
      />
    );
  }

  if (submissionQuery.isLoading || !submission) {
    return <PageSkeleton />;
  }

  const language: "en" | "es" = i18n.language.startsWith("es") ? "es" : "en";
  const title = t("history.detail.title");
  const owning = context?.memberships.find((m) => m.org_id === submission.org_id) ?? active;
  const timezone = owning?.org_timezone;

  // The redacted definition rides the submission-detail body; the wire ships
  // it non-null for a completed submission, but never render an absent
  // document — guard before handing it to the bridge (the bridge itself
  // narrows, it does not null-check).
  const definition =
    submission.definition !== null && submission.definition !== undefined
      ? parseFormDefinition(submission.definition)
      : undefined;
  const renderable = definition !== undefined && Array.isArray(definition.sections);

  const photos = evidenceImages(submission.attachments ?? []);

  return (
    <div className="flex flex-col gap-5 lg:gap-6">
      <PageTitle
        title={title}
        sub={t("history.detail.tested", {
          date: testDay(submission, timezone, i18n.language),
        })}
        back={{ label: t("nav.tabs.history"), link: historyLink() }}
        trail={[{ label: t("nav.tabs.history"), link: historyLink() }, { label: title }]}
      />

      {/* Only an accepted test has official report forms; any other state
          answers the list read 404, so the card is never mounted for one. */}
      {submission.status === "accepted" ? (
        <OfficialReportCard submissionId={submission.id} />
      ) : null}

      <Card>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted">{t("tests.completedAt")}</dt>
            <dd className="mt-0.5 text-ink">
              {submission.submitted_at ? (
                formatLongInstantDay(submission.submitted_at, timezone, i18n.language)
              ) : (
                <EmptyValue />
              )}
            </dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-muted">{t("tests.privacyNote")}</p>
      </Card>

      <Card title={t("tests.detailTitle")}>
        {/* Read-only mount of the shared renderer. The facilityRefSlot
            resolves a facility_ref answer to the premise name through the
            customer facility read. No attachmentSlot: image evidence renders
            in its own gallery card below, image documents only by the
            redaction contract — PDF documents and signatures never surface
            on this page. */}
        {renderable && definition ? (
          <FormRenderer
            definition={definition}
            answers={submission.responses}
            language={language}
            facilityRefSlot={({ value }) => <FacilityRefValue facilityId={value} />}
          />
        ) : (
          <p className="text-sm text-muted">{t("tests.loadDetailFailed")}</p>
        )}
      </Card>

      <Card title={t("tests.evidenceTitle")}>
        {photos.length === 0 ? (
          <EmptyState title={t("tests.noEvidence")} variant="inline" />
        ) : (
          <div className="flex flex-wrap gap-4">
            {photos.map((photo) => (
              <AuthedImage
                key={photo.id}
                submissionId={submission.id}
                attachmentId={photo.id}
                alt={t("tests.evidenceTitle")}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
